import React, { useEffect, useMemo, useState } from "react";
import apiService from "../../services/api";
import styles from "./StudentVisibilityManager.module.css";

const StudentVisibilityManager = ({ courseId, courseName, onClose }) => {
  const [studentVisibility, setStudentVisibility] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [isLoadingVisibility, setIsLoadingVisibility] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const visibilityCategories = useMemo(
    () => [
      { name: "Assignments", key: "show_assignments" },
      { name: "Quizzes", key: "show_quizzes" },
      { name: "Attendance", key: "show_attendance" },
      { name: "Announcements", key: "show_announcements" },
      { name: "Recordings", key: "show_recordings" },
      { name: "Readings", key: "show_readings" },
      { name: "Slides", key: "show_slides" },
      { name: "Syllabus", key: "show_syllabus" },
      { name: "WhatsApp Group", key: "show_whatsapp_group" },
      { name: "Virtual Session", key: "show_virtual_session" },
      { name: "Electives", key: "show_electives" },
    ],
    []
  );

  const filteredStudents = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return students;

    return students.filter((student) => {
      const fullName = `${student.first_name || ""} ${student.last_name || ""}`
        .trim()
        .toLowerCase();
      const email = (student.email || "").toLowerCase();
      return fullName.includes(query) || email.includes(query);
    });
  }, [students, searchTerm]);

  const fetchJson = async (endpoint, options = {}) => {
    const method = (options.method || "GET").toUpperCase();
    const headers = {
      ...(options.headers || {}),
    };

    const token = localStorage.getItem("faithinsight_token");
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (["POST", "PUT", "DELETE", "PATCH"].includes(method)) {
      const csrfToken = await apiService.fetchCSRFToken();
      if (!csrfToken) {
        throw new Error(
          "Failed to obtain CSRF token. Please refresh the page and try again."
        );
      }
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(`${apiService.baseURL}${endpoint}`, {
      credentials: "include",
      ...options,
      method,
      headers,
    });

    const contentType = response.headers.get("content-type") || "";

    if (!response.ok) {
      const bodyText = await response.text();
      console.error("Visibility fetch failed", {
        url: response.url,
        status: response.status,
        contentType,
      });

      if (response.status === 401 || response.status === 403) {
        throw new Error("You don’t have access to this resource. Please refresh and sign in again.");
      }

      throw new Error(
        `Request failed with status ${response.status}. Body: ${bodyText.slice(0, 200)}`
      );
    }

    if (!contentType.includes("application/json")) {
      const bodyText = await response.text();
      console.error("Visibility fetch expected JSON", {
        url: response.url,
        status: response.status,
        contentType,
      });
      throw new Error(
        `Expected JSON but got ${contentType || "unknown"}. Body: ${bodyText.slice(0, 200)}`
      );
    }

    return response.json();
  };

  // Modal UX
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  useEffect(() => {
    const loadStudents = async () => {
      try {
        setIsLoadingStudents(true);
        setError("");
        setSuccessMessage("");
        const data = await fetchJson(`/courses/${courseId}/students/visibility`);
        const normalizedStudents = Array.isArray(data?.students)
          ? data.students.map((student) => ({
              ...student,
              id: student.id ?? student.student_id,
            }))
          : [];
        setStudents(normalizedStudents);
      } catch (e) {
        setError(e?.message || "Failed to load students");
      } finally {
        setIsLoadingStudents(false);
      }
    };
    if (courseId) loadStudents();
  }, [courseId]);

  useEffect(() => {
    const loadVisibility = async () => {
      if (!selectedStudent?.id) return;
      try {
        setIsLoadingVisibility(true);
        setError("");
        setSuccessMessage("");
        const data = await fetchJson(`/courses/${courseId}/students/${selectedStudent.id}/visibility`);
        setStudentVisibility(data?.visibility || data);
      } catch (e) {
        setError(e?.message || "Failed to load student visibility");
      } finally {
        setIsLoadingVisibility(false);
      }
    };
    loadVisibility();
  }, [courseId, selectedStudent]);

  const toggleItem = (categoryKey) => {
    setStudentVisibility((prev) => ({
      ...prev,
      [categoryKey]: !prev?.[categoryKey],
    }));
  };

  const save = async () => {
    if (!selectedStudent?.id) return;
    try {
      setIsSaving(true);
      setError("");
      setSuccessMessage("");
      await fetchJson(`/courses/${courseId}/students/${selectedStudent.id}/visibility`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(studentVisibility),
      });
      setSuccessMessage("Visibility saved successfully.");
      onClose?.();
    } catch (e) {
      setError(e?.message || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.header}>
          <div>
            <div className={styles.title}>Student Access Settings</div>
            <div className={styles.subtitle}>
              <span className={styles.courseName}>{courseName}</span>
              <span className={styles.dot}>•</span>
              <span className={styles.courseId}>Course ID: {courseId}</span>
            </div>
          </div>

          <button className={styles.iconBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.left}>
            <div className={styles.panelTitle}>Select student</div>
            <input
              className={styles.searchInput}
              type="search"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              aria-label="Search students"
            />
            {isLoadingStudents ? (
              <div className={styles.stateText}>Loading students…</div>
            ) : (
              <div className={styles.studentList}>
                {filteredStudents.length === 0 ? (
                  <div className={styles.stateText}>No students match your search.</div>
                ) : (
                  filteredStudents.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={`${styles.studentItem} ${selectedStudent?.id === s.id ? styles.studentItemActive : ""}`}
                      onClick={() => setSelectedStudent(s)}
                    >
                      <div className={styles.studentName}>
                        {s.first_name} {s.last_name}
                      </div>
                      <div className={styles.studentMeta}>{s.email}</div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className={styles.right}>
            <div className={styles.sectionHeader}>
              <div>
                <div className={styles.sectionTitle}>Visibility Controls</div>
                <div className={styles.sectionHint}>
                  Toggle sections for the selected student.
                </div>
              </div>
            </div>

            {error ? <div className={styles.errorBanner}>{error}</div> : null}
            {successMessage ? <div className={styles.successBanner}>{successMessage}</div> : null}

            <div className={styles.panel}>
              {!selectedStudent ? (
                <div className={styles.stateText}>Choose a student to manage visibility.</div>
              ) : isLoadingVisibility ? (
                <div className={styles.stateText}>Loading visibility…</div>
              ) : !studentVisibility ? (
                <div className={styles.stateText}>No visibility data.</div>
              ) : (
                <div className={styles.toggleGrid}>
                  {visibilityCategories.map((cat) => (
                    <div key={cat.key} className={styles.toggleRow}>
                      <div className={styles.toggleMeta}>
                        <div className={styles.toggleName}>{cat.name}</div>
                        <div className={styles.toggleSub}>
                          {studentVisibility?.[cat.key] ? "Visible" : "Hidden"}
                        </div>
                      </div>

                      <label className={styles.switch}>
                        <input
                          type="checkbox"
                          checked={!!studentVisibility?.[cat.key]}
                          onChange={() => toggleItem(cat.key)}
                        />
                        <span className={styles.slider} />
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.footer}>
              <button className={styles.secondaryBtn} onClick={onClose}>
                Close
              </button>
              <button className={styles.primaryBtn} onClick={save} disabled={!selectedStudent || isSaving}>
                {isSaving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentVisibilityManager;
